import React, { useEffect, useRef } from "react";
import { useParams } from "react-router";
import { ExpandedCard } from "@sscs/ui";
import { getClassName } from "@sscs/utils";
import { useAppDispatch, useAppSelector } from "Hooks";
import { scrollElementIntoView } from "Utils";
import {
  expandedRowsUpdated,
  focusedRowUpdated,
  selectFocusedRow,
  selectIsRowExpanded,
} from "../../store";
import { Insight } from "../../types";
import { getTotalReleaseStatusCount } from "../../utils";
import Details from "./Details/Details";
import styles from "./insightCard.module.scss";
import Insights from "./Insights/Insights";
import Summary from "./Summary/Summary";
import Title from "./Title/Title";
import Versions from "./Versions/Versions";

type InsightCardProps = {
  issue: Insight;
  rowIndex: number;
};

const InsightCard = ({
  issue,
  rowIndex,
}: InsightCardProps): React.JSX.Element => {
  const dispatch = useAppDispatch();
  const { groupId } = useParams();
  const isExpanded = useAppSelector((state) =>
    selectIsRowExpanded(state, groupId, issue.id),
  );
  const focusedRow = useAppSelector((state) =>
    selectFocusedRow(state, groupId),
  );
  const cardRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isExpanded && cardRef.current) {
      scrollElementIntoView(cardRef.current, 3); // 2px outline + 1px border
    }
  }, [isExpanded]);

  return (
    <ExpandedCard.Card
      ref={cardRef}
      id={`issue-${rowIndex}`}
      isExpanded={isExpanded}
      isFocused={focusedRow === issue.id}
      onChange={(expanded) => {
        if (groupId) {
          dispatch(
            expandedRowsUpdated({
              groupId,
              issueId: issue.id,
              expand: expanded,
            }),
          );
        }
      }}
      onFocus={() => {
        if (groupId) {
          dispatch(focusedRowUpdated({ groupId, issueId: issue.id }));
        }
      }}
      className={getClassName(styles.card, {
        [styles["card-expanded"]]: isExpanded,
      })}
    >
      <ExpandedCard.Title className={styles.title}>
        <Title issue={issue} />
      </ExpandedCard.Title>
      <ExpandedCard.Summary>
        <Summary issue={issue} />
      </ExpandedCard.Summary>
      <ExpandedCard.Details className={styles["details-content"]}>
        <Details issue={issue} />
        <Insights issue={issue} />
        <Versions
          issueId={issue.id}
          issueType={issue.issue_type}
          affectedVersions={getTotalReleaseStatusCount(
            issue.counters.affected_versions,
          )}
        />
      </ExpandedCard.Details>
    </ExpandedCard.Card>
  );
};

export default InsightCard;
